/* eslint-disable max-len */
import { Card, Col, Modal, Row, Table, Tooltip } from 'antd';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';

import ButtonComponent from 'components/atoms/ButtonComponent';
import Paragraph from 'components/atoms/Paragraph';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';

import useNavigateWorkaround from 'hooks/useNavigateWorkaround';
import useProfily from 'hooks/useProfily';

import { EStavZadostAmlz } from 'services/spravaEmitenta/historickeZadosti';
import {
    EPristupStav,
    IOsobyGetInput,
    IOsobyGetResp,
    osobyC2Aktualizovat,
    osobyC2Get,
    osobyC2Odebrat,
    osobyC2Zrusit,
    zadostPravoCheck,
    zadostPravoVyjadreni,
    zadostPravoZrusit
} from 'services/spravaEmitenta/zadostPravo';

import { ENotificationType } from 'enums/notificationType';
import { EProfilyTyp } from 'enums/profilyTyp';
import routePathEnum from 'enums/routePathEnum';

import getLanguageObject from 'utils/getLanguageObject';

interface IOsobyPrehled {}

const OsobyPrehled: React.FC<IOsobyPrehled> = () => {
    const { confirm } = Modal;
    const { navigateWorkaround } = useNavigateWorkaround();
    const { aktivniFirma, NotificationComponent, zadostAgenda, menuProfile, language, profily } = useContext(Context);
    const { setLevel } = useProfily();
    const [errStatus, setErrStatus] = useState<number>();
    const [loading, setLoading] = useState<boolean>(false);
    const [refetchData, setRefetchData] = useState<boolean>(false);
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [modalVyjadreni, setModalVyjadreni] = useState<string>('');
    const [zadosti, setZadosti] = useState<IOsobyGetResp>();
    const [pagination, setPagination] = useState<IOsobyGetInput>({
        order: 'id:desc',
        limit: 10,
        offset: 0
    });
    const isLegalEntity = profily?.typprihlaseni === 'DLT';

    useEffect(() => {
        setLoading(true);
        setErrStatus(0);
        if (menuProfile?.c2edit) {
            setLoading(true);
            osobyC2Get(pagination)
                .then(resp => setZadosti(resp))
                .finally(() => setLoading(false));
        } else {
            zadostPravoCheck()
                .then(resp => {
                    setLevel('zadostAgenda', resp);
                    setLoading(false);
                    if (resp.pristupc2?.stav === EPristupStav.KDOPL) {
                        zadostPravoVyjadreni(resp?.pristupc2?.id!).then(data => setModalVyjadreni(data.vyjadreni));
                    }
                })
                .catch((err: any) => {
                    setErrStatus(err.response?.status);
                })
                .finally(() => setLoading(false));
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refetchData, pagination]);

    return (
        <>
            {errStatus ? (
                <Row
                    justify="center"
                    align="middle"
                    style={{
                        height: '100%'
                    }}
                >
                    <Col span={16} flex={'0 0 auto'}>
                        {errStatus === 404 ? (
                            <Title text="CHYBA_AMLZ_NEVEDENO" level={3} />
                        ) : errStatus ? (
                            <Title text="SLUZBA_NEDOSTUPNA" level={3} />
                        ) : null}
                    </Col>
                </Row>
            ) : zadostAgenda?.pristupc2?.stav === 'ZADNA' && !menuProfile?.c2edit ? (
                <>
                    <Title
                        level={3}
                        text={
                            isLegalEntity
                                ? 'EMITENT_PREHLED_TITLE_LEGALENTITY'
                                : aktivniFirma?.typ === EProfilyTyp.ADMEMIT
                                  ? 'EMITENT_PREHLED_TITLE_ADMEMIT'
                                  : 'EMITENT_PREHLED_TITLE_EMITENT'
                        }
                    />
                    <Paragraph
                        style={{
                            display: 'block',
                            maxWidth: 800
                        }}
                        text={
                            isLegalEntity
                                ? 'EMITENT_PREHLED_TEXT_LEGALENTITY'
                                : aktivniFirma?.typ === EProfilyTyp.ADMEMIT
                                  ? 'EMITENT_PREHLED_TEXT_ADMEMIT'
                                  : 'EMITENT_PREHLED_TEXT_EMITENT'
                        }
                    />
                </>
            ) : menuProfile?.c2edit ? (
                <>
                    <Title
                        level={3}
                        text={
                            isLegalEntity
                                ? 'EMITENT_PREHLED_TITLE_LEGALENTITY'
                                : aktivniFirma?.typ === EProfilyTyp.ADMEMIT
                                  ? 'EMITENT_PREHLED_TITLE_ADMEMIT'
                                  : 'EMITENT_PREHLED_TITLE_EMITENT'
                        }
                    />
                    <Table
                        loading={loading}
                        columns={[
                            {
                                ellipsis: true,
                                title: <TranslateText>JMENO</TranslateText>,
                                key: 'jmeno',
                                dataIndex: 'jmeno',
                                render: (_a, row) =>
                                    (row.jmeno || '') +
                                    ' ' +
                                    (row.prijmeni || '') +
                                    (row.duplicita
                                        ? ' (' +
                                          getLanguageObject(language).AMLZ_C2_DUPLICITA +
                                          ' ' +
                                          row.duplicita +
                                          ')'
                                        : '')
                            },
                            {
                                ellipsis: true,
                                title: <TranslateText>TELEFON</TranslateText>,
                                key: 'telefon',
                                dataIndex: 'telefon',
                                width: 125
                            },
                            {
                                ellipsis: true,
                                title: <TranslateText>EMAIL</TranslateText>,
                                key: 'email',
                                dataIndex: 'email'
                            },
                            {
                                ellipsis: {
                                    showTitle: false
                                },
                                title: <TranslateText>AUTENTIZACE</TranslateText>,
                                key: 'autprvek',
                                dataIndex: 'autprvek',
                                width: 136,
                                render: (autprvek: string) => (
                                    <Tooltip
                                        mouseEnterDelay={0.5}
                                        overlayClassName="tableTooltip"
                                        placement="bottomRight"
                                        title={getLanguageObject(language)[`TYP_PRIHLASENI_${autprvek}`]}
                                    >
                                        {getLanguageObject(language)[`TYP_PRIHLASENI_${autprvek}`]}
                                    </Tooltip>
                                )
                            },
                            {
                                ellipsis: {
                                    showTitle: false
                                },
                                title: <TranslateText>STAV_ZPRACOVANI</TranslateText>,
                                key: 'stav',
                                dataIndex: 'stav',
                                render: (stav: string, row) => (
                                    <>
                                        <Tooltip
                                            mouseEnterDelay={0.5}
                                            overlayClassName="tableTooltip"
                                            placement="bottomRight"
                                            title={
                                                getLanguageObject(language)[`STAVZADOSTI_SIMPLE_${stav}`] +
                                                (row.datum &&
                                                (stav === EStavZadostAmlz.UZIVCEKA || stav === EStavZadostAmlz.UZIVDOPL)
                                                    ? ' (' + dayjs(row.datum).format('DD.MM.YYYY HH:mm') + ')'
                                                    : '')
                                            }
                                        >
                                            {getLanguageObject(language)[`STAVZADOSTI_SIMPLE_${stav}`] +
                                                (row.datum &&
                                                (stav === EStavZadostAmlz.UZIVCEKA || stav === EStavZadostAmlz.UZIVDOPL)
                                                    ? ' (' + dayjs(row.datum).format('DD.MM.YYYY HH:mm') + ')'
                                                    : '')}
                                        </Tooltip>
                                    </>
                                )
                            },
                            {
                                dataIndex: 'edit',
                                key: 'edit',
                                render: (_, row, index) => {
                                    return (
                                        <Row
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'right'
                                            }}
                                        >
                                            {row.idzadosti &&
                                                row.stav !== EStavZadostAmlz.CDCPKONTR &&
                                                row.stav !== EStavZadostAmlz.AUTKONTROLA &&
                                                row.stav !== EStavZadostAmlz.CDCPKEKONTR && (
                                                    <Row
                                                        onClick={() => {
                                                            setLevel('zadostAgenda', {
                                                                pristupc2: {
                                                                    id: row.idzadosti!,
                                                                    idosoby: row.idosoby,
                                                                    idprofilu: row.id
                                                                }
                                                            });
                                                            navigateWorkaround(
                                                                `/firma/${aktivniFirma?.iducet}${
                                                                    isLegalEntity
                                                                        ? routePathEnum.DLT_PO_ZASTUPUJICI_OSOBY_EDITACE
                                                                        : routePathEnum.EMITENT_ZASTUPUJICI_OSOBY_EDITACE
                                                                }`
                                                            );
                                                        }}
                                                        justify="center"
                                                    >
                                                        <a id={`zobrazitBtn${index}`}>
                                                            <TranslateText>
                                                                {row.stav &&
                                                                [
                                                                    EStavZadostAmlz.HOTOVO,
                                                                    EStavZadostAmlz.HOTOVOIIS,
                                                                    EStavZadostAmlz.ZAMITNUTO,
                                                                    EStavZadostAmlz.ZNEPLATNENO
                                                                ].includes(row.stav)
                                                                    ? 'ZOBRAZIT'
                                                                    : row.stav === EStavZadostAmlz.UZIVDOPL
                                                                      ? 'ZKONTROLUJTE'
                                                                      : 'BUTTON_UPRAVIT'}
                                                            </TranslateText>
                                                        </a>
                                                    </Row>
                                                )}
                                            {
                                                // row.stav === EStavZadostAmlz.CDCPKDOPL ||
                                                // row.stav === EStavZadostAmlz.UZIVDOPL ||
                                                row.stav &&
                                                    [
                                                        EStavZadostAmlz.HOTOVO,
                                                        EStavZadostAmlz.ZAMITNUTO,
                                                        EStavZadostAmlz.HOTOVOIIS,
                                                        EStavZadostAmlz.INI,
                                                        EStavZadostAmlz.ZNEPLATNENO
                                                    ].includes(row.stav) && (
                                                        <Row
                                                            style={{
                                                                marginLeft: 14
                                                            }}
                                                            onClick={() => {
                                                                setLoading(true);
                                                                osobyC2Aktualizovat(row.id)
                                                                    .then(resp => {
                                                                        if (resp.stav === 'ZALOZENO') {
                                                                            setLevel('zadostAgenda', {
                                                                                pristupc2: {
                                                                                    id: resp.idzadosti,
                                                                                    idosoby: resp.idosoby,
                                                                                    idprofilu: row.id
                                                                                },
                                                                                osobaData: {
                                                                                    autprvek: row.autprvek,
                                                                                    email: row.email,
                                                                                    jmeno: row.jmeno,
                                                                                    prijmeni: row.prijmeni,
                                                                                    telefon: row.telefon
                                                                                }
                                                                            });
                                                                            navigateWorkaround(
                                                                                `/firma/${aktivniFirma?.iducet}${
                                                                                    isLegalEntity
                                                                                        ? routePathEnum.DLT_PO_ZASTUPUJICI_OSOBY_EDITACE
                                                                                        : routePathEnum.EMITENT_ZASTUPUJICI_OSOBY_EDITACE
                                                                                }`
                                                                            );
                                                                        } else {
                                                                            NotificationComponent({
                                                                                title:
                                                                                    getLanguageObject(language)[
                                                                                        'NELZE_POKRACOVAT_' + resp.stav
                                                                                    ] +
                                                                                        ' ' +
                                                                                        resp.uzivatel || '',
                                                                                type: ENotificationType.ERROR
                                                                            });
                                                                        }
                                                                    })
                                                                    .finally(() => setLoading(false));
                                                            }}
                                                            justify="center"
                                                        >
                                                            <a id={`zobrazitBtn${index}`}>
                                                                <TranslateText>AKTUALIZOVAT</TranslateText>
                                                            </a>
                                                        </Row>
                                                    )
                                            }
                                            {(row?.stav === EStavZadostAmlz.CDCPKDOPL ||
                                                row?.stav === EStavZadostAmlz.DOPL) &&
                                                modalVyjadreni && (
                                                    <Row
                                                        style={{
                                                            marginLeft: 14
                                                        }}
                                                        onClick={() => {
                                                            setLoading(true);
                                                            zadostPravoVyjadreni(zadostAgenda?.pristupc2?.id!)
                                                                .then(data => {
                                                                    setModalVyjadreni(data.vyjadreni);
                                                                    setModalOpen(true);
                                                                })
                                                                .finally(() => setLoading(false));
                                                        }}
                                                        justify="center"
                                                    >
                                                        <a id={`zobrazitBtn${index}`}>
                                                            <TranslateText>VYJADRENI_CDCP</TranslateText>
                                                        </a>
                                                    </Row>
                                                )}

                                            {row.id + '' !== profily?.id + '' &&
                                            (row.stav === EStavZadostAmlz.HOTOVO ||
                                                row.stav === EStavZadostAmlz.HOTOVOIIS ||
                                                row.stav === EStavZadostAmlz.INI) &&
                                            row.id ? (
                                                <Row
                                                    style={{
                                                        marginLeft: 14
                                                    }}
                                                    onClick={() => {
                                                        confirm({
                                                            title: <TranslateText>POTVRDENI</TranslateText>,
                                                            content: `${
                                                                getLanguageObject(language).DELETE_CONFIRM_C2_TEXT
                                                            }`,
                                                            cancelText: <TranslateText>NE</TranslateText>,
                                                            okText: <TranslateText>ANO</TranslateText>,
                                                            onOk() {
                                                                setLoading(true);
                                                                osobyC2Zrusit(row.id)
                                                                    .then(() => setRefetchData(pv => !pv))
                                                                    .finally(() => setLoading(false));
                                                            }
                                                        });
                                                    }}
                                                    justify="center"
                                                >
                                                    <a id={`zrusitBtn${index}`}>
                                                        <TranslateText>ODSTRANIT_OSOBU</TranslateText>
                                                    </a>
                                                </Row>
                                            ) : row.idosoby &&
                                              row.id + '' !== profily?.id + '' &&
                                              (row.stav === EStavZadostAmlz.UZIVCEKA ||
                                                  row.stav === EStavZadostAmlz.UZIVDOPL ||
                                                  row.stav === EStavZadostAmlz.DOPL ||
                                                  row.stav === EStavZadostAmlz.ROZP ||
                                                  row.stav === EStavZadostAmlz.CDCPKDOPL) ? (
                                                <Row
                                                    style={{
                                                        marginLeft: 14
                                                    }}
                                                    onClick={() => {
                                                        confirm({
                                                            title: <TranslateText>POTVRDENI</TranslateText>,
                                                            content: `${
                                                                getLanguageObject(language).DELETE_CONFIRM_C2_TEXT_ALT
                                                            }`,
                                                            cancelText: <TranslateText>NE</TranslateText>,
                                                            okText: <TranslateText>ANO</TranslateText>,
                                                            onOk() {
                                                                setLoading(true);
                                                                osobyC2Odebrat(row.idosoby!, row.idzadosti!)
                                                                    .then(() => setRefetchData(pv => !pv))
                                                                    .finally(() => setLoading(false));
                                                            }
                                                        });
                                                    }}
                                                    justify="center"
                                                >
                                                    <a id={`zrusitBtn${index}`}>
                                                        <TranslateText>ZRUSIT_ZADOST</TranslateText>
                                                    </a>
                                                </Row>
                                            ) : (
                                                <></>
                                            )}
                                        </Row>
                                    );
                                }
                            }
                        ]}
                        rowKey={(_, index) => index || 0}
                        dataSource={zadosti?.osoby.filter(e => e.stav !== EStavZadostAmlz.ODEBRAT)}
                        locale={{
                            emptyText: <TranslateText>ZADNE_UDAJE</TranslateText>
                        }}
                        pagination={{
                            total: zadosti?.count || 0,
                            defaultPageSize: pagination.limit || 10,
                            current:
                                pagination?.limit && pagination?.offset
                                    ? Math.ceil((pagination?.offset + 1) / pagination?.limit)
                                    : 1,
                            onChange: page => {
                                setPagination(pv => {
                                    return {
                                        ...pv,
                                        offset: (page - 1) * (pagination?.limit || 10),
                                        limit: pagination.limit,
                                        order: pagination.order
                                    };
                                });
                            },

                            showSizeChanger: true
                        }}
                        rowClassName={(_a, i) => {
                            const classes = [];
                            if (i % 2) classes.push('tableRowGray');
                            return classes.join(' ');
                        }}
                    />
                </>
            ) : (
                <LoadingWrapper loading={loading}>
                    <Title
                        text={
                            isLegalEntity
                                ? 'EMITENT_PREHLED_TITLE_LEGALENTITY'
                                : aktivniFirma?.typ === EProfilyTyp.ADMEMIT
                                  ? 'EMITENT_PREHLED_TITLE_ADMEMIT'
                                  : 'EMITENT_PREHLED_TITLE_EMITENT'
                        }
                        level={4}
                    />{' '}
                    <Row gutter={16}>
                        <Col
                            span={12}
                            style={{
                                marginBottom: 36
                            }}
                        >
                            <Card className="dash-card" title={''}>
                                <Title text="AMLZ_PREHLED_DATUM" level={5} />
                                <p>{dayjs(zadostAgenda?.pristupc2?.datumzmeny).format('DD.MM.YYYY')}</p>
                                <Title text="EMP_PODANE_ZADOSTI_STAV" level={5} />
                                <p>
                                    {zadostAgenda?.pristupc2?.stav && (
                                        <TranslateText>
                                            {`STAVZADOSTI_SIMPLE_${zadostAgenda?.pristupc2?.stav
                                                .toUpperCase()
                                                .split(' ')
                                                .join('_')}`}
                                        </TranslateText>
                                    )}
                                </p>
                                <Row className="button-row">
                                    {(zadostAgenda?.pristupc2?.stav === EPristupStav.ROZP ||
                                        zadostAgenda?.pristupc2?.stav === EPristupStav.KDOPL) && (
                                        <>
                                            <ButtonComponent
                                                buttontext={'POKRACOVAT'}
                                                id="emitentPrehledPokracovatBtn"
                                                onClick={() => {
                                                    navigateWorkaround(
                                                        `/firma/${aktivniFirma?.iducet}${
                                                            isLegalEntity
                                                                ? routePathEnum.DLT_PO_ZASTUPUJICI_OSOBY_ZADOST
                                                                : routePathEnum.EMITENT_ZASTUPUJICI_OSOBY_ZADOST
                                                        }`
                                                    );
                                                }}
                                            />

                                            <ButtonComponent
                                                id="emitentPrehledZrusitZadostBtn"
                                                buttontext={'ZRUSIT_ZADOST'}
                                                onClick={() => {
                                                    setLoading(true);
                                                    zadostPravoZrusit(zadostAgenda?.pristupc2?.id || 0).then(() => {
                                                        NotificationComponent({
                                                            title: 'ZRUSIT_ZADOST',
                                                            description: 'ZRUSIT_ZADOST_SUCCESS',
                                                            type: ENotificationType.SUCCESS
                                                        });
                                                        setRefetchData(pv => !pv);
                                                    });
                                                }}
                                            />
                                        </>
                                    )}

                                    {zadostAgenda?.pristupc2?.stav === EPristupStav.KDOPL && modalVyjadreni && (
                                        <ButtonComponent
                                            buttontext={'VYJADRENI_CDCP'}
                                            onClick={() => {
                                                setModalOpen(true);
                                            }}
                                            id="emitentPrehledVyjadreniBtn"
                                        />
                                    )}
                                </Row>
                            </Card>
                        </Col>
                    </Row>
                    <Modal
                        title={<TranslateText>{'VYJADRENI_CDCP'}</TranslateText>}
                        open={modalOpen}
                        onCancel={() => setModalOpen(false)}
                        afterClose={() => {
                            setModalOpen(false);
                        }}
                        footer={
                            <Row className="button-row-modal">
                                <ButtonComponent
                                    buttontext="ZAVRIT"
                                    onClick={() => {
                                        setModalOpen(false);
                                    }}
                                    id="emitentPrehledZavritBtn"
                                />
                            </Row>
                        }
                    >
                        <Row>
                            <Paragraph text={modalVyjadreni} />
                        </Row>
                    </Modal>
                </LoadingWrapper>
            )}
        </>
    );
};

export default OsobyPrehled;
